/**
 * ArchetypePicker — switch a disciplined-compose scene's archetype.
 *
 * Shows the golden archetypes, RECOMMENDED set filtered by the scene's narrationShape (intent
 * routing), with previews. Picking one does not edit the outline directly: it hands a natural-language
 * change request to the agent orchestrator (the chat), which re-plans the scene and updates the outline.
 */
import { useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import { X, Check, Sparkles } from 'lucide-react';
import { KUBICLE_ARCHETYPES, type KubicleArchetype } from '@/config/kubicleArchetypes';

interface ArchetypePickerProps {
  currentArchetypeKey?: string;
  narrationShape?: string;
  onConfirm: (archetype: KubicleArchetype) => void;
  onClose: () => void;
}

export function ArchetypePicker({ currentArchetypeKey, narrationShape, onConfirm, onClose }: ArchetypePickerProps) {
  const [showAll, setShowAll] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | undefined>(currentArchetypeKey);

  const recommended = useMemo(
    () => KUBICLE_ARCHETYPES.filter(a => narrationShape && a.narrationShapes.includes(narrationShape)),
    [narrationShape],
  );
  const list = showAll || recommended.length === 0 ? KUBICLE_ARCHETYPES : recommended;
  const selected = KUBICLE_ARCHETYPES.find(a => a.key === selectedKey);
  const canSwitch = !!selected && selected.key !== currentArchetypeKey;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div
        className="w-full max-w-4xl max-h-[85vh] flex flex-col rounded-2xl bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 dark:border-zinc-800">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Switch archetype</h3>
            <p className="text-xs text-gray-500 dark:text-zinc-500 mt-0.5">
              {narrationShape ? (
                <>Recommended for <span className="font-medium text-primary dark:text-accent-foreground">{narrationShape}</span> scenes. The agent re-plans the shot-list and updates the outline.</>
              ) : (
                <>Pick an archetype. The agent re-plans the shot-list and updates the outline.</>
              )}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-zinc-800">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Filter toggle */}
        {recommended.length > 0 && (
          <div className="px-5 pt-3">
            <div className="inline-flex items-center gap-0.5 p-0.5 rounded-lg bg-gray-100 dark:bg-zinc-800 text-xs">
              <button
                onClick={() => setShowAll(false)}
                className={cn('px-2.5 py-1 rounded-md font-medium transition-colors', !showAll ? 'bg-white dark:bg-zinc-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-zinc-400')}
              >
                Recommended ({recommended.length})
              </button>
              <button
                onClick={() => setShowAll(true)}
                className={cn('px-2.5 py-1 rounded-md font-medium transition-colors', showAll ? 'bg-white dark:bg-zinc-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-zinc-400')}
              >
                All ({KUBICLE_ARCHETYPES.length})
              </button>
            </div>
          </div>
        )}

        {/* Grid */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {list.map(a => {
              const isSelected = a.key === selectedKey;
              const isCurrent = a.key === currentArchetypeKey;
              return (
                <button
                  key={a.key}
                  onClick={() => setSelectedKey(a.key)}
                  className={cn(
                    'group text-left rounded-xl overflow-hidden border transition-all',
                    isSelected
                      ? 'border-primary ring-2 ring-primary/30'
                      : 'border-gray-200 dark:border-zinc-800 hover:border-gray-300 dark:hover:border-zinc-700',
                  )}
                >
                  <div className="relative aspect-video bg-gray-100 dark:bg-zinc-800">
                    <img src={a.previewUrl} alt={a.key} loading="lazy" className="w-full h-full object-cover"
                      onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                    {isCurrent && (
                      <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded bg-black/60 text-white text-[10px] font-medium">Current</span>
                    )}
                    {isSelected && (
                      <span className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </span>
                    )}
                  </div>
                  <div className="px-2.5 py-2">
                    <div className="text-[12px] font-mono font-medium text-gray-800 dark:text-zinc-200 truncate">{a.key}</div>
                    <div className="text-[10px] text-gray-400 dark:text-zinc-500 truncate">{a.family}{a.hasCharacter ? ' · character' : ''}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-gray-100 dark:border-zinc-800">
          <p className="text-[11px] text-gray-400 dark:text-zinc-500 truncate">
            {selected ? <span className="font-mono">{selected.key}</span> : 'Select an archetype'}
          </p>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-3 py-1.5 text-xs font-medium rounded-lg text-gray-600 dark:text-zinc-300 hover:bg-gray-100 dark:hover:bg-zinc-800">
              Cancel
            </button>
            <button
              onClick={() => selected && onConfirm(selected)}
              disabled={!canSwitch}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors',
                canSwitch ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'bg-gray-100 dark:bg-zinc-800 text-gray-400 dark:text-zinc-600 cursor-not-allowed',
              )}
            >
              <Sparkles className="w-3.5 h-3.5" />
              Switch & re-plan
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
